import type MapScale from '@/Enums/Map/MapScale.enum';
import type ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import type { IGamTotalsReport } from '@/Interfaces/ReportingDashboard/Gam/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IGamState } from '@/Interfaces/Slices/ReportingDashboard/Gam/State.interface';
import { defaultGamTotals, initialGamState } from '@/constants/reportingDashboard/gam';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/reportingDashboard/slice';
import { pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { type AsyncThunk, type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  getGamCampaignOptions,
  getGamCityBoundaries,
  getGamCongressionalDistrictBoundaries,
  getGamCountyBoundaries,
  getGamDmaBoundaries,
  getGamStateBoundaries,
  getGamTotalsReach,
  getGamZipBoundaries,
  getTotalsReport,
  reportMap,
} from './gamThunks';

// Adding a new scale = append one entry.
type BoundaryDataKey =
  | 'stateBoundariesData'
  | 'dmaBoundariesData'
  | 'countyBoundariesData'
  | 'cityBoundariesData'
  | 'zipBoundariesData'
  | 'congressionalDistrictBoundariesData';
type BoundaryHandler = {
  thunk: AsyncThunk<unknown, IAbortSignalThunkArg, Record<string, never>>;
  dataKey: BoundaryDataKey;
  requestIdKey: `${BoundaryDataKey}RequestId`;
};
const boundaryHandlers: BoundaryHandler[] = [
  { thunk: getGamStateBoundaries, dataKey: 'stateBoundariesData', requestIdKey: 'stateBoundariesDataRequestId' },
  { thunk: getGamDmaBoundaries, dataKey: 'dmaBoundariesData', requestIdKey: 'dmaBoundariesDataRequestId' },
  { thunk: getGamCountyBoundaries, dataKey: 'countyBoundariesData', requestIdKey: 'countyBoundariesDataRequestId' },
  { thunk: getGamCityBoundaries, dataKey: 'cityBoundariesData', requestIdKey: 'cityBoundariesDataRequestId' },
  { thunk: getGamZipBoundaries, dataKey: 'zipBoundariesData', requestIdKey: 'zipBoundariesDataRequestId' },
  {
    thunk: getGamCongressionalDistrictBoundaries,
    dataKey: 'congressionalDistrictBoundariesData',
    requestIdKey: 'congressionalDistrictBoundariesDataRequestId',
  },
];

export const reportingDashboardGam = createSlice({
  name: 'reportingDashboardGam',
  initialState: initialGamState,
  reducers: {
    resetGamReportsLoadingState: state => {
      Object.entries(state).forEach(([key]) => {
        // Campaign options are a filter SOURCE, not a report tile: nothing
        // re-requests them on apply, so blanket-resetting their request id
        // strands the Campaign selector disabled with options already loaded
        // (#4235). Their lifecycle belongs to the filter's own effect.
        if (key.endsWith('RequestId') && key !== 'campaignOptionsRequestId') {
          (state[key as keyof IGamState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
      // Filter apply re-evaluates BE cache hits — let the next round of
      // fulfillments re-flag dataIsRefreshing if any snapshot is still missing.
      state.dataIsRefreshing = false;
      // Reach is filter-specific; wipe the previous value so the user sees
      // a spinner while the new query runs rather than the stale number.
      state.totalsReach = null;
      state.totalsReachTimedOut = false;
    },
    // Persist the map-scale switcher choice on the GAM slice so the user's
    // selection survives across re-renders and selectGamMapScale can read
    // it without coupling to the shared printPdfOptions state.
    setGamMapScale: (state, { payload }: PayloadAction<MapScale>) => {
      state.mapScale = payload;
    },
    // ImpressionsByType Delivery/Attribution toggle. GAM has no attribution
    // surface yet — the toggle ships for parity; Attribution view renders
    // empty rows with the standard "no data" overlay.
    setGamViewType: (state, { payload }: PayloadAction<ViewType>) => {
      state.viewType = payload;
    },
  },
  extraReducers: builder => {
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    builder
      .addCase(getGamCampaignOptions.pending, pendingCaseHandler('campaignOptions', []))
      .addCase(getGamCampaignOptions.rejected, rejectedCaseHandler('campaignOptions', []))
      .addCase(getGamCampaignOptions.fulfilled, (state, action) => {
        if (state.campaignOptionsRequestId !== action.meta.requestId) return;
        state.campaignOptionsRequestId = null;
        state.campaignOptions = action.payload;
      });

    builder
      .addCase(getTotalsReport.pending, pendingCaseHandler('totals'))
      .addCase(getTotalsReport.rejected, (state, action) => {
        if (state.totalsRequestId !== action.meta.requestId) return;
        state.totalsRequestId = null;
        if (action.error?.name === 'CanceledError') return;
        state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
      })
      .addCase(getTotalsReport.fulfilled, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          const { totals, dataIsRefreshing } = action.payload as IGamTotalsReport;

          state.totalsRequestId = null;
          if (dataIsRefreshing) {
            state.errorCardMessage = '';
            state.totals = defaultGamTotals;
            state.dataIsRefreshing = true;
            return;
          }
          state.totals = totals ?? defaultGamTotals;
          if (totals?.impressions != null) {
            // Snapshot KPIs are ready — don't keep Impressions/VCR spinning while
            // breakdown tiles prefetch in the background.
            state.dataIsRefreshing = false;
          }
          if (!totals) {
            state.errorCardMessage = "We couldn't find any data. Please adjust your filters and try again.";
          } else if (!state.totals.reach || !state.totals.frequency) {
            // GAM may emit Reach/Frequency = 0 before unique-visitor join warms
            // up. Match the Simplifi pattern: render "—" with a tooltip rather
            // than a confident "0".
            state.totals = {
              ...state.totals,
              reach: null,
              frequency: null,
            };
          }
        }
      });

    builder
      .addCase(getGamTotalsReach.pending, (state, action) => {
        state.totalsReachRequestId = action.meta.requestId;
        state.totalsReachTimedOut = false;
      })
      .addCase(getGamTotalsReach.rejected, (state, action) => {
        if (state.totalsReachRequestId === action.meta.requestId) {
          state.totalsReachRequestId = null;
          if (action.error?.name !== 'CanceledError') {
            // Network/5xx — same UX as a GAM timeout so the tile shows the retry tooltip.
            state.totalsReachTimedOut = true;
          }
        }
      })
      .addCase(getGamTotalsReach.fulfilled, (state, action) => {
        if (state.totalsReachRequestId !== action.meta.requestId) return;
        state.totalsReachRequestId = null;
        const { reach, frequency, timedOut } = action.payload;
        state.totalsReachTimedOut = !!timedOut;
        state.totalsReach = timedOut ? null : { reach, frequency };
      });

    boundaryHandlers.forEach(({ thunk, dataKey, requestIdKey }) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          state[requestIdKey] = action.meta.requestId;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          if (state[requestIdKey] === action.meta.requestId) {
            state[requestIdKey] = null;
            state[dataKey] = action.payload as object | null;
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          if (state[requestIdKey] === action.meta.requestId) {
            state[requestIdKey] = null;
          }
        });
    });

    // Breakdown tiles can prefetch while TOTAL KPIs are already resolved.
    // getTotalsReport.fulfilled owns the KPI-side dataIsRefreshing flag.
    builder.addMatcher(
      action =>
        typeof action.type === 'string' &&
        action.type.startsWith('reportingDashboardGam/') &&
        action.type.endsWith('/fulfilled') &&
        action.type !== `${getTotalsReport.fulfilled.type}`,
      (state, action) => {
        if (action.payload?.dataIsRefreshing) {
          state.dataIsRefreshing = true;
        }
      },
    );
  },
});

export const { resetGamReportsLoadingState, setGamMapScale, setGamViewType } = reportingDashboardGam.actions;

export default reportingDashboardGam.reducer;
